import type { Event, EventHint } from '@sentry/nextjs'

// Errors coming from browser/OS/extension environments we don't control and can't fix.
// These are noise, not actionable bugs - drop them before they reach Sentry.
export const SENTRY_IGNORE_ERRORS = [
  // Android WebView JS-interface teardown artifact: the injected Java object
  // is garbage-collected mid-call when the WebView is backgrounded/destroyed.
  // Native-side lifecycle issue, not something the web app can prevent.
  // Also its send(): "Error invoking send: Java bridge method invocation error".
  /Error invoking (postMessage|send)\b/i,

  // iOS in-app browsers calling a missing native bridge. The app never uses window.webkit.
  /window\.webkit\.messageHandlers/,

  // Meta's in-app browser (Instagram/Facebook) injects an inline script that throws when
  // its own window._META object isn't there. The app never references _META itself.
  // WebKit: "...evaluating 'window._META.IABInnerFrame'", Chromium: "...reading 'IABInnerFrame'".
  /window\._META\b|IABInnerFrame/,
  // Facebook/Instagram iOS autofill script.
  /_AutofillCallbackHandler/,

  // Browser extensions injecting themselves into the page
  /Invalid call to runtime\.sendMessage/i,
  /Extension context invalidated/i,

  // Network blips outside our control (client offline, mobile network drop, proxy resets)
  /socket hang up/i,
  /Network Error/i,
  /ECONNRESET/i,
  /ECONNABORTED/i,
  /Failed to fetch/i,
  /Load failed/i,

  // Known-benign Next.js/browser quirks
  /Invariant: attempted to hard navigate to the same URL/i,
  // Thrown on purpose by the route guards to cancel navigation on unsaved changes.
  /Route change aborted/i,
  /ResizeObserver loop limit exceeded/i,
  /Non-Error promise rejection captured/i,
]

const getAxiosStatusCode = (hint: EventHint): number | undefined => {
  const error = hint.originalException as { isAxiosError?: boolean; response?: { status?: number } }
  return error?.isAxiosError ? error.response?.status : undefined
}

// Functions and files injected by in-app browsers and third-party scripts, not by this app.
// Their messages are generic, so they're matched by the frame that threw.
const INJECTED_FUNCTIONS = new Set([
  'dispatchToBridge',
  'sendPostMessage',
  'sendDataToNative',
  'sendPageHideMessage',
  'logLoginFormFocused',
  'setContactAutofillValuesFromBridge',
])
const INJECTED_FILE = /\/executors\/\d+\.js$/

const isFromInjectedScript = (event: Event) =>
  (event.exception?.values ?? []).some((exception) => {
    // Frames are oldest first: the last one threw.
    const frames = exception.stacktrace?.frames ?? []
    const frame = frames[frames.length - 1]
    return Boolean(
      (frame?.function && INJECTED_FUNCTIONS.has(frame.function)) ||
        (frame?.filename && INJECTED_FILE.test(frame.filename)),
    )
  })

// AxiosErrors for expected client-side responses (bad input, missing resource, auth)
// are not application bugs - keep them out of the error stream but still record them
// as warnings so we retain visibility without noisy alerting.
export const sentryBeforeSend = (event: Event, hint: EventHint): Event | null => {
  if (isFromInjectedScript(event)) return null

  const status = getAxiosStatusCode(hint)

  if (status && status >= 400 && status < 500) {
    event.level = 'warning'
  }

  return event
}
